import React from 'react';
import { DialogProviderContextModel } from "models/DialogProviderContext"
import * as Dialogs from 'components/dialogs';
import { DialogHandle, Image, Folder } from 'models';
import { containsSpecialCharacters } from 'utils';
import { Progress } from 'models';

export function buildFolderNameDialog(dialogProvider: DialogProviderContextModel, onCreate: (folderName: string) => Promise<string[] | undefined>, defaultText?: string, title?: string): DialogHandle {
    const validate = (folderName: string): string[] => {
        const trimmed = folderName ? folderName.trim() : '';
        const errors = [] as string[];

        if (trimmed.length <= 0) {
            errors.push('Please provide a folder name');
        }

        if (containsSpecialCharacters(trimmed)) {
            errors.push('Special characters are not allowed');
        }

        if (trimmed.length > 50) {
            errors.push(`Folder names must be 50 characters or less. The name you entered is ${trimmed.length} characters.`)
        }

        return errors;
    }

    return new DialogHandle(dialogProvider,
        (handle: DialogHandle) => (
            <Dialogs.CreateFolderDialog onSave={onCreate} onValidate={validate} onCancel={handle.close} defaultText={defaultText} title={title} />
        )
    );
}

export function buildDeleteFolderDialog(dialogProvider: DialogProviderContextModel, onConfirm: () => Promise<string[] | undefined>): DialogHandle {
    return new DialogHandle(dialogProvider,
        (handle: DialogHandle) => (
            <Dialogs.ConfirmDeleteDialog onConfirm={onConfirm} onCancel={handle.close} title="Delete folder" message="Are you sure you want to delete this folder?" />
        )
    );
}

export function buildDeleteImagesDialog(dialogProvider: DialogProviderContextModel, images: Image[], onConfirm: () => Promise<string[] | undefined>): DialogHandle {
    const title = images.length === 1 ? `Delete ${images[0].fileName}` : 'Delete images';
    const message = images.length === 1 ? `Are you sure you want to permanently delete ${images[0].fileName}?` : 'Are you sure you want to delete the selected images?';

    return new DialogHandle(dialogProvider,
        (handle: DialogHandle) => (
            <Dialogs.ConfirmDeleteDialog onConfirm={onConfirm} onCancel={handle.close} title={title} message={message} />
        )
    );
}

export function buildMoveImagesDialog(dialogProvider: DialogProviderContextModel, currentFolderId: string, images: Image[], onMove: () => Promise<string[] | undefined>): DialogHandle {
    const title = images.length === 1 ? `Move ${images[0].fileName}` : 'Move images';
    const message = images.length === 1 ? `Select where you would like to move ${images[0].fileName}` : 'Select where you would like to move the selected images';

    return new DialogHandle(dialogProvider,
        (handle: DialogHandle) => (
            <Dialogs.MoveItemDialog onMove={onMove} onCancel={handle.close} title={title} message={message} currentFolderId={currentFolderId} />
        )
    );
}

export function buildMoveFolderDialog(dialogProvider: DialogProviderContextModel, currentFolderId: string, folder: Folder, onMove: () => Promise<string[] | undefined>): DialogHandle {
    const title = `Move ${folder.name}`;
    const message = 'Select where you would like to move this folder';

    return new DialogHandle(dialogProvider,
        (handle: DialogHandle) => (
            <Dialogs.MoveItemDialog onMove={onMove} onCancel={handle.close} title={title} message={message} currentFolderId={currentFolderId} />
        )
    );
}

export function buildConfirmUploadDialog(dialogProvider: DialogProviderContextModel, files: FileList, folderId: string): DialogHandle {
    return new DialogHandle(dialogProvider,
        (handle: DialogHandle) => (
            <Dialogs.UploadDialog onClose={handle.close} files={files} dialogContext={dialogProvider} folderId={folderId} />
        )
    );
}

export function buildUploadProgressDialog(dialogProvider: DialogProviderContextModel, progress: Progress): [DialogHandle, (progress: Progress) => void] {
    const render = (progress: Progress) => {
        return (handle: DialogHandle) => (
            <Dialogs.ProgressDialog onClose={handle.close} progress={progress} />
        )
    }

    const handle = new DialogHandle(dialogProvider, render(progress));

    const update = (progress: Progress) => {
        handle.update(render(progress));
    }

    return [
        handle,
        update
    ];
}

export function buildConfirmDuplicateImageDialog(dialogProvider: DialogProviderContextModel, imageNames: string[], onConfirm: () => void) {
    const title = imageNames.length === 1 ? `Duplicate image found` : 'Duplicate images found';
    const message = imageNames.length === 1 ? `This upload contains a duplicate image ${imageNames[0]}. Do you want to continue uploading?` : `This upload contains ${imageNames.length} images. Do you want to continue uploading?`;

    return new DialogHandle(dialogProvider,
        (handle: DialogHandle) => (
            <Dialogs.ConfirmContinueDialog onConfirm={onConfirm} onCancel={handle.close} title={title} message={message} />
        )
    );
}